import React, { useEffect, useMemo, useState } from "react";
import { GetFileExtension } from "datalign-module-template/helpers/GetFileExtension";

import Input from "models/input/input";
import Asset from "datalign-module-template/models/asset";
import Boundary from "models/geospatial/boundary";
import Point from "models/geospatial/point";

import {
  AUDIO_EXTENSIONS,
  CODE_EXTENSIONS,
  IMAGE_EXTENSIONS,
  TEXT_EXTENSIONS,
  VIDEO_EXTENSIONS,
} from "datalign-module-template/components/extensionsList";
import InfoOptionType from "models/info-option-type";
import BaseResultCard from "./base-result-card/BaseResultCard";
import NtResultCard from "./nt-result-card/NtResultCard";

const SearchResultCard = ({
  input,
  showData,
  assets,
  boundaries,
  points,
  infoOption,
}: {
  input: Input;
  showData: string[];
  assets: Asset[];
  boundaries: Boundary[];
  points: Point[];
  infoOption: InfoOptionType;
}) => {
  const [open, setOpen] = useState(
    infoOption === "detail" ||
      process.env.REACT_APP_STYLING_THEME === "nrdata-orig",
  );
  const showStandardMetadata = useMemo(
    () =>
      showData
        ? showData.filter(
            (key) => input.metadata[key]?.data_type === "standard",
          )
        : [],
    [showData],
  );

  const showDatatypeMetadata = useMemo(
    () =>
      showData
        ? showData.filter(
            (key) => input.metadata[key]?.data_type !== "standard",
          )
        : [],
    [showData],
  );

  const imageFiles = assets.filter(
    (file): file is Asset =>
      !!file.name && IMAGE_EXTENSIONS.includes(GetFileExtension(file.name)),
  );

  const videoFiles = assets.filter(
    (file): file is Asset =>
      !!file.name && VIDEO_EXTENSIONS.includes(GetFileExtension(file.name)),
  );

  const audioFiles = assets.filter(
    (file): file is Asset =>
      !!file.name && AUDIO_EXTENSIONS.includes(GetFileExtension(file.name)),
  );

  const textFiles = assets.filter(
    (file): file is Asset =>
      !!file.name &&
      [
        ...TEXT_EXTENSIONS,
        ...CODE_EXTENSIONS,
        ".csv",
        ".json",
        ".geojson",
        ".pdf",
      ].includes(GetFileExtension(file.name)),
  );

  useEffect(() => {
    setOpen(infoOption === "detail");
  }, [infoOption]);

  return ["FAF-nt"].includes(process.env.REACT_APP_STYLING_THEME ?? "") ? (
    <NtResultCard
      input={input}
      showStandardMetadata={showStandardMetadata}
      showDatatypeMetadata={showDatatypeMetadata}
      boundaries={boundaries}
      points={points}
      open={open}
      HandleOpen={setOpen}
      imageFiles={imageFiles}
      videoFiles={videoFiles}
      audioFiles={audioFiles}
      textFiles={textFiles}
    />
  ) : (
    <BaseResultCard
      input={input}
      showStandardMetadata={showStandardMetadata}
      showDatatypeMetadata={showDatatypeMetadata}
      boundaries={boundaries}
      points={points}
      open={open}
      HandleOpen={setOpen}
      imageFiles={imageFiles}
      videoFiles={videoFiles}
      audioFiles={audioFiles}
      textFiles={textFiles}
    />
  );
};

export default SearchResultCard;
